import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Users, 
  MousePointer,
  Eye,
  Target,
  Plus,
  Play,
  Pause,
  CheckCircle,
  Clock,
  ChevronLeft,
  Loader2,
  Sparkles,
  AlertTriangle,
  Trophy,
  PieChart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart as RePieChart,
  Pie,
  Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = {
  uk: { name_ar: "بريطانيا", flag: "🇬🇧" },
  india: { name_ar: "الهند", flag: "🇮🇳" },
  china: { name_ar: "الصين", flag: "🇨🇳" },
  saudi: { name_ar: "السعودية", flag: "🇸🇦" },
  russia: { name_ar: "روسيا", flag: "🇷🇺" },
  usa: { name_ar: "أمريكا", flag: "🇺🇸" },
  iraq: { name_ar: "العراق", flag: "🇮🇶" },
  uae: { name_ar: "الإمارات", flag: "🇦🇪" },
  pakistan: { name_ar: "باكستان", flag: "🇵🇰" },
  germany: { name_ar: "ألمانيا", flag: "🇩🇪" }
};

const STATUS_COLORS = {
  draft: "bg-slate-500",
  active: "bg-emerald-500",
  paused: "bg-amber-500",
  completed: "bg-blue-500"
};

const STATUS_LABELS = {
  draft: "مسودة",
  active: "نشط",
  paused: "متوقف",
  completed: "مكتمل"
};

const CHART_COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

const CampaignTracking = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [countryAnalytics, setCountryAnalytics] = useState([]);
  const [insights, setInsights] = useState(null);
  const [activeTab, setActiveTab] = useState("campaigns");
  const [showPerformanceDialog, setShowPerformanceDialog] = useState(false);
  const [performanceForm, setPerformanceForm] = useState({
    country_code: "",
    actual_spend_usd: "",
    actual_reach: "",
    actual_clicks: "",
    actual_leads: "",
    actual_conversions: "",
    notes: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, analyticsRes, insightsRes] = await Promise.all([
        axios.get(`${API}/campaigns/tracked`),
        axios.get(`${API}/analytics/countries`),
        axios.get(`${API}/analytics/insights`)
      ]);
      setCampaigns(campaignsRes.data);
      setCountryAnalytics(analyticsRes.data);
      setInsights(insightsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignAnalysis = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/campaigns/tracked/${campaignId}/analysis`);
      setAnalysis(response.data);
    } catch (error) {
      console.error("Error loading analysis:", error);
      setAnalysis(null);
    }
  };

  const updateCampaignStatus = async (campaignId, newStatus) => {
    try {
      await axios.put(`${API}/campaigns/tracked/${campaignId}/status`, { status: newStatus });
      toast.success(`تم تحديث الحالة إلى: ${STATUS_LABELS[newStatus]}`);
      loadData();
    } catch (error) {
      toast.error("خطأ في تحديث الحالة");
    }
  };

  const submitPerformance = async () => {
    if (!selectedCampaign || !performanceForm.country_code) {
      toast.error("يرجى اختيار الدولة");
      return;
    }

    try {
      await axios.post(`${API}/campaigns/tracked/${selectedCampaign.id}/performance`, {
        campaign_id: selectedCampaign.id,
        country_code: performanceForm.country_code,
        actual_spend_usd: parseFloat(performanceForm.actual_spend_usd) || 0,
        actual_reach: parseInt(performanceForm.actual_reach) || 0,
        actual_clicks: parseInt(performanceForm.actual_clicks) || 0,
        actual_leads: parseInt(performanceForm.actual_leads) || 0,
        actual_conversions: parseInt(performanceForm.actual_conversions) || 0,
        notes: performanceForm.notes
      });
      
      toast.success("تم حفظ بيانات الأداء");
      setShowPerformanceDialog(false);
      setPerformanceForm({
        country_code: "",
        actual_spend_usd: "",
        actual_reach: "",
        actual_clicks: "",
        actual_leads: "",
        actual_conversions: "",
        notes: ""
      });
      loadData();
      if (selectedCampaign) {
        loadCampaignAnalysis(selectedCampaign.id);
      }
    } catch (error) {
      toast.error("خطأ في حفظ البيانات");
    }
  };

  const selectCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    loadCampaignAnalysis(campaign.id);
    setActiveTab("details");
  };

  const formatNumber = (num) => new Intl.NumberFormat('ar-AE').format(num);
  const formatCurrency = (num) => `$${num?.toFixed(2) || '0.00'}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} data-testid="back-to-dashboard">
            <ChevronLeft className="w-4 h-4 ml-1" />
            العودة
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-white">تتبع أداء الحملات</h2>
            <p className="text-slate-400 text-sm">راقب وحلل أداء حملاتك الإعلانية</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
            <BarChart3 className="w-4 h-4 ml-2" />
            الحملات
          </TabsTrigger>
          <TabsTrigger value="details" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900" disabled={!selectedCampaign}>
            <Target className="w-4 h-4 ml-2" />
            تفاصيل الحملة
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
            <PieChart className="w-4 h-4 ml-2" />
            التحليلات
          </TabsTrigger>
        </TabsList>

        {/* Campaigns List Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          {campaigns.length === 0 ? (
            <Card className="bg-slate-900/50 border-slate-700">
              <CardContent className="p-12 text-center">
                <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">لا توجد حملات متتبعة</h3>
                <p className="text-slate-400 mb-4">أنشئ حملة جديدة من صفحة التوصيات لبدء التتبع</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {campaigns.map((campaign) => (
                <Card 
                  key={campaign.id} 
                  className="bg-slate-900/50 border-slate-700 hover:border-amber-500/30 transition-colors cursor-pointer"
                  onClick={() => selectCampaign(campaign)}
                  data-testid={`campaign-${campaign.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-3 h-3 rounded-full ${STATUS_COLORS[campaign.status]}`} />
                        <div>
                          <h4 className="text-white font-semibold">
                            {campaign.property?.property_type} - {campaign.property?.area}
                          </h4>
                          <p className="text-sm text-slate-400">
                            ميزانية: ${campaign.budget?.total_budget_usd} | 
                            {new Date(campaign.created_at).toLocaleDateString('ar-AE')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${STATUS_COLORS[campaign.status]} text-white`}>
                          {STATUS_LABELS[campaign.status]}
                        </Badge>
                        {Object.keys(campaign.performance || {}).length > 0 && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                            {Object.keys(campaign.performance).length} دول
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Campaign Details Tab */}
        <TabsContent value="details" className="space-y-6">
          {selectedCampaign && (
            <>
              {/* Campaign Header */}
              <Card className="bg-slate-900/50 border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {selectedCampaign.property?.property_type} في {selectedCampaign.property?.area}
                      </h3>
                      <p className="text-slate-400">
                        سعر العقار: {formatNumber(selectedCampaign.property?.price_aed)} درهم
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedCampaign.status === "draft" && (
                        <Button 
                          size="sm" 
                          onClick={() => updateCampaignStatus(selectedCampaign.id, "active")}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="start-campaign-btn"
                        >
                          <Play className="w-4 h-4 ml-1" />
                          بدء الحملة
                        </Button>
                      )}
                      {selectedCampaign.status === "active" && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateCampaignStatus(selectedCampaign.id, "paused")}
                            data-testid="pause-campaign-btn"
                          >
                            <Pause className="w-4 h-4 ml-1" />
                            إيقاف مؤقت
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => updateCampaignStatus(selectedCampaign.id, "completed")}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="complete-campaign-btn"
                          >
                            <CheckCircle className="w-4 h-4 ml-1" />
                            إنهاء
                          </Button>
                        </>
                      )}
                      {selectedCampaign.status === "paused" && (
                        <Button 
                          size="sm" 
                          onClick={() => updateCampaignStatus(selectedCampaign.id, "active")}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Play className="w-4 h-4 ml-1" />
                          استئناف
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <DollarSign className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                      <div className="text-lg font-bold text-white">${selectedCampaign.budget?.total_budget_usd}</div>
                      <div className="text-xs text-slate-400">الميزانية</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <Target className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                      <div className="text-lg font-bold text-white">
                        {selectedCampaign.recommendations?.countries?.length || 0}
                      </div>
                      <div className="text-xs text-slate-400">دول مستهدفة</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <BarChart3 className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                      <div className="text-lg font-bold text-white">
                        {Object.keys(selectedCampaign.performance || {}).length}
                      </div>
                      <div className="text-xs text-slate-400">دول مسجلة</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <Clock className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                      <div className={`text-lg font-bold ${STATUS_COLORS[selectedCampaign.status].replace('bg-', 'text-')}`}>
                        {STATUS_LABELS[selectedCampaign.status]}
                      </div>
                      <div className="text-xs text-slate-400">الحالة</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Add Performance Data */}
              <Card className="bg-slate-900/50 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Plus className="w-5 h-5 text-amber-500" />
                      تسجيل أداء فعلي
                    </CardTitle>
                    <Dialog open={showPerformanceDialog} onOpenChange={setShowPerformanceDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="add-performance-btn">
                          <Plus className="w-4 h-4 ml-1" />
                          إضافة بيانات
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-slate-900 border-slate-700">
                        <DialogHeader>
                          <DialogTitle className="text-white">تسجيل أداء الحملة</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label className="text-slate-300">الدولة</Label>
                            <Select 
                              value={performanceForm.country_code}
                              onValueChange={(v) => setPerformanceForm(prev => ({...prev, country_code: v}))}
                            >
                              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                <SelectValue placeholder="اختر الدولة" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                {Object.entries(COUNTRIES).map(([code, data]) => (
                                  <SelectItem key={code} value={code} className="text-white">
                                    {data.flag} {data.name_ar}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-slate-300">الإنفاق الفعلي ($)</Label>
                              <Input
                                type="number"
                                value={performanceForm.actual_spend_usd}
                                onChange={(e) => setPerformanceForm(prev => ({...prev, actual_spend_usd: e.target.value}))}
                                className="bg-slate-800 border-slate-700 text-white"
                                data-testid="input-spend"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">الوصول</Label>
                              <Input
                                type="number"
                                value={performanceForm.actual_reach}
                                onChange={(e) => setPerformanceForm(prev => ({...prev, actual_reach: e.target.value}))}
                                className="bg-slate-800 border-slate-700 text-white"
                                data-testid="input-reach"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">النقرات</Label>
                              <Input
                                type="number"
                                value={performanceForm.actual_clicks}
                                onChange={(e) => setPerformanceForm(prev => ({...prev, actual_clicks: e.target.value}))}
                                className="bg-slate-800 border-slate-700 text-white"
                                data-testid="input-clicks"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-300">العملاء المحتملين</Label>
                              <Input
                                type="number"
                                value={performanceForm.actual_leads}
                                onChange={(e) => setPerformanceForm(prev => ({...prev, actual_leads: e.target.value}))}
                                className="bg-slate-800 border-slate-700 text-white"
                                data-testid="input-leads"
                              />
                            </div>
                          </div>
                          <Button 
                            onClick={submitPerformance} 
                            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900"
                            data-testid="submit-performance-btn"
                          >
                            حفظ البيانات
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {Object.keys(selectedCampaign.performance || {}).length === 0 ? (
                    <p className="text-slate-400 text-center py-8">
                      لم يتم تسجيل أي بيانات أداء بعد. أضف بيانات من Meta Ads Manager.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(selectedCampaign.performance).map(([countryCode, perf]) => (
                        <div key={countryCode} className="bg-slate-800/50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{COUNTRIES[countryCode]?.flag}</span>
                              <span className="text-white font-semibold">{COUNTRIES[countryCode]?.name_ar}</span>
                            </div>
                            <Badge className="badge-amber">${perf.actual_spend_usd}</Badge>
                          </div>
                          <div className="grid grid-cols-4 gap-3 text-center">
                            <div>
                              <div className="text-emerald-400 font-bold">{formatNumber(perf.actual_reach)}</div>
                              <div className="text-xs text-slate-500">الوصول</div>
                            </div>
                            <div>
                              <div className="text-blue-400 font-bold">{formatNumber(perf.actual_clicks)}</div>
                              <div className="text-xs text-slate-500">النقرات</div>
                            </div>
                            <div>
                              <div className="text-amber-400 font-bold">{perf.actual_leads}</div>
                              <div className="text-xs text-slate-500">العملاء</div>
                            </div>
                            <div>
                              <div className="text-purple-400 font-bold">{perf.ctr}%</div>
                              <div className="text-xs text-slate-500">CTR</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* AI Analysis */}
              {analysis && analysis.ai_analysis && (
                <Card className="bg-gradient-to-br from-amber-500/10 to-emerald-500/5 border-amber-500/30">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-amber-500" />
                      تحليل الذكاء الاصطناعي
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-300 leading-relaxed mb-4">{analysis.ai_analysis}</p>
                    
                    {analysis.recommendations && analysis.recommendations.length > 0 && (
                      <div className="space-y-2 mt-4">
                        <h4 className="text-white font-semibold">توصيات التحسين:</h4>
                        {analysis.recommendations.map((rec, i) => (
                          <div 
                            key={i} 
                            className={`flex items-start gap-2 p-3 rounded-lg ${
                              rec.priority === 'high' ? 'bg-red-500/10 border border-red-500/30' : 'bg-slate-800/50'
                            }`}
                          >
                            {rec.priority === 'high' ? (
                              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                            ) : (
                              <TrendingUp className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                            )}
                            <span className="text-slate-300 text-sm">{rec.message_ar}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {/* Insights Cards */}
          {insights && insights.insights && insights.insights.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              {insights.insights.map((insight, i) => (
                <Card 
                  key={i} 
                  className={`border ${
                    insight.type === 'best_performer' 
                      ? 'bg-emerald-500/10 border-emerald-500/30' 
                      : 'bg-amber-500/10 border-amber-500/30'
                  }`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    {insight.type === 'best_performer' ? (
                      <Trophy className="w-8 h-8 text-emerald-400" />
                    ) : (
                      <AlertTriangle className="w-8 h-8 text-amber-400" />
                    )}
                    <p className="text-white">{insight.message_ar}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Country Performance Chart */}
          {countryAnalytics.length > 0 ? (
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">أداء الدول</CardTitle>
                <CardDescription className="text-slate-400">
                  إحصائيات مجمعة من جميع الحملات
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={countryAnalytics} layout="vertical">
                      <XAxis type="number" stroke="#64748b" />
                      <YAxis 
                        type="category" 
                        dataKey="country_name_ar" 
                        stroke="#64748b" 
                        width={80}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'rgba(15, 23, 42, 0.95)', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => {
                          if (name === 'total_leads') return [value, 'العملاء'];
                          if (name === 'avg_cost_per_lead') return [`$${value}`, 'تكلفة/عميل'];
                          return [value, name];
                        }}
                      />
                      <Bar dataKey="total_leads" fill="#f59e0b" name="العملاء" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Country Table */}
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-right text-slate-400 py-2 px-3">الدولة</th>
                        <th className="text-right text-slate-400 py-2 px-3">الإنفاق</th>
                        <th className="text-right text-slate-400 py-2 px-3">العملاء</th>
                        <th className="text-right text-slate-400 py-2 px-3">CTR</th>
                        <th className="text-right text-slate-400 py-2 px-3">تكلفة/عميل</th>
                      </tr>
                    </thead>
                    <tbody>
                      {countryAnalytics.map((country) => (
                        <tr key={country.country_code} className="border-b border-slate-800">
                          <td className="py-3 px-3">
                            <span className="text-lg ml-2">{country.flag}</span>
                            <span className="text-white">{country.country_name_ar}</span>
                          </td>
                          <td className="py-3 px-3 text-amber-400">${country.total_spend_usd}</td>
                          <td className="py-3 px-3 text-emerald-400">{country.total_leads}</td>
                          <td className="py-3 px-3 text-blue-400">{country.avg_ctr}%</td>
                          <td className="py-3 px-3 text-purple-400">${country.avg_cost_per_lead}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-slate-900/50 border-slate-700">
              <CardContent className="p-12 text-center">
                <PieChart className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">لا توجد بيانات تحليلية</h3>
                <p className="text-slate-400">سجّل أداء الحملات لرؤية التحليلات المجمعة</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignTracking;
